import type {
  ContingencyPlanCreateRequest,
  ContingencyPlanDTO,
  ContingencyPlanUpdateRequest,
  ResidentBody
} from '@lmc/ts-common'
import { Form } from '@lmc/ui-kit'
import type { FormInstance } from 'antd'
import { showToast } from 'common/utils'
import type { Dayjs } from 'dayjs'
import useContingencyPlan from 'hooks/useContingencyPlan'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { handleOnFinishFailed } from '../../../utils/utils'
import { AdvancedStatementsFormCard } from './FormCards/AdvancedStatements'
import OtherDetailsFormCard from './FormCards/OtherDetails'
import PlanNameFormCard from './FormCards/PlanName'
import { ScenariosFormCard } from './FormCards/Scenarios'

type ContingencyPlanFormProps = {
  create?: boolean
  form: FormInstance
  plan?: ContingencyPlanDTO | undefined
  consentFormIds?: string[]
  riskAssessmentIds?: string[]
  isConsentFuturePlanEnabled?: boolean
  isRiskAssessmentFuturePlanEnabled?: boolean
  setIsCreateLoading?: (value: boolean) => void
  setIsUpdateLoading?: (value: boolean) => void
  setIsDirty: (value: boolean) => void
  children?: ReactNode
}

type FormType = {
  nextReviewDate: Dayjs
}

const ContingencyPlanForm = ({
  create,
  form,
  plan,
  consentFormIds = [],
  riskAssessmentIds = [],
  isConsentFuturePlanEnabled = false,
  isRiskAssessmentFuturePlanEnabled = false,
  setIsCreateLoading,
  setIsUpdateLoading,
  setIsDirty,
  children
}: ContingencyPlanFormProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const navigate = useNavigate()

  const { id: residentID, contingencyPlanID } = useParams()
  const { createContingencyPlan, updateContingencyPlan } = useContingencyPlan({
    contingencyPlanID,
    onCreateSuccess: () => {
      showToast({
        title: t(supConstants.FUTURE_PLAN_CREATED)
      })
      navigate(`../`, { relative: 'path' })
    },
    onUpdateSuccess: () => {
      showToast({
        title: t(supConstants.FUTURE_PLAN_UPDATED)
      })
      navigate(`../`, { relative: 'path' })
    }
  })

  const handleOnCreateFinish = async (
    values: Partial<ContingencyPlanCreateRequest>
  ) => {
    const convertedValues = {
      ...values,
      ...(isRiskAssessmentFuturePlanEnabled
        ? {
            riskAssessments: riskAssessmentIds
          }
        : {}),
      ...(isConsentFuturePlanEnabled
        ? {
            consentForms: consentFormIds
          }
        : {}),
      resident: residentID,
      scenarios: values.scenarios || [],
      agreedWithOtherPerson: values.agreedWithServiceUser
        ? ''
        : values.agreedWithOtherPerson
    }
    try {
      setIsCreateLoading?.(true)
      setIsDirty(false)
      await createContingencyPlan(
        convertedValues as ContingencyPlanCreateRequest
      )
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
    }
    setIsCreateLoading?.(false)
  }

  const handleOnUpdateFinish = async (values: ContingencyPlanUpdateRequest) => {
    const convertedValues = {
      ...values,
      ...(isRiskAssessmentFuturePlanEnabled
        ? {
            riskAssessments: riskAssessmentIds
          }
        : {}),
      ...(isConsentFuturePlanEnabled
        ? {
            consentForms: consentFormIds
          }
        : {}),
      scenarios: values.scenarios || [],
      agreedWithOtherPerson: values.agreedWithServiceUser
        ? ''
        : values.agreedWithOtherPerson
    }
    try {
      setIsUpdateLoading?.(true)
      setIsDirty(false)
      await updateContingencyPlan(convertedValues)
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
    }
    setIsUpdateLoading?.(false)
  }

  const handleFormValuesChange = (values: Partial<ResidentBody>) => {
    if (values) {
      setIsDirty(true)
    }
  }

  const initialValues: Partial<ContingencyPlanDTO> = {
    ...plan,
    nextReviewDate: undefined
  }

  return (
    <Form<FormType>
      form={form}
      initialValues={initialValues}
      onFinish={create ? handleOnCreateFinish : handleOnUpdateFinish}
      onValuesChange={handleFormValuesChange}
      onFinishFailed={({ errorFields }) => handleOnFinishFailed(errorFields)}
    >
      <PlanNameFormCard />
      <ScenariosFormCard />
      <AdvancedStatementsFormCard />
      {children}
      <OtherDetailsFormCard plan={plan} />
    </Form>
  )
}

export default ContingencyPlanForm
